import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Receipt, Plus, Check, X, Upload, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExpensesPage = () => {
  const { isManager } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newExpense, setNewExpense] = useState({
    category: '', amount: '', description: '', vendor: '', date: new Date().toISOString().split('T')[0], receipt_url: ''
  });
  const [billFile, setBillFile] = useState(null);

  const fetchExpenses = useCallback(async () => {
    try {
      let url = `${API_URL}/api/expenses`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setBillFile(file);
    }
  };

  const uploadBill = async () => {
    if (!billFile) return null;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', billFile);
      formData.append('category', 'expense_bill');
      const res = await axios.post(`${API_URL}/api/upload?category=expense_bill`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.path;
    } catch (err) {
      toast.error('Failed to upload bill');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let receiptUrl = newExpense.receipt_url;
      if (billFile) {
        receiptUrl = await uploadBill();
      }
      await axios.post(`${API_URL}/api/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount),
        receipt_url: receiptUrl || '',
      }, { withCredentials: true });
      toast.success('Expense submitted! Admin has been notified.');
      setIsAddDialogOpen(false);
      setNewExpense({ category: '', amount: '', description: '', vendor: '', date: new Date().toISOString().split('T')[0], receipt_url: '' });
      setBillFile(null);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (expenseId, status) => {
    try {
      await axios.put(`${API_URL}/api/expenses/${expenseId}/approve?status=${status}`, {}, { withCredentials: true });
      toast.success(`Expense ${status}`);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    }
  };

  const getStatusBadge = (status) => {
    const classes = { approved: 'badge-success', rejected: 'badge-danger', pending: 'badge-warning' };
    return <Badge className={classes[status]}>{status}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const labels = { saas: 'SaaS', travel: 'Travel', office: 'Office', equipment: 'Equipment' };
    return <Badge variant="outline">{labels[category] || category}</Badge>;
  };

  const totalPending = expenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.amount, 0);
  const totalApproved = expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Receipt className="w-7 h-7 text-primary" />Expense Claims</h1>
          <p className="text-muted-foreground">Submit and track expense reimbursements</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-expense-btn"><Plus className="w-4 h-4" />Submit Expense</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Submit Expense</DialogTitle></DialogHeader>
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newExpense.category} onValueChange={(v) => setNewExpense({ ...newExpense, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saas">SaaS Subscription</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="office">Office Supplies</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (INR)</Label>
                  <Input type="number" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} placeholder="0.00" required data-testid="expense-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={newExpense.date} onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input value={newExpense.vendor} onChange={(e) => setNewExpense({ ...newExpense, vendor: e.target.value })} placeholder="e.g., AWS, Notion" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} placeholder="Describe the expense..." required />
              </div>
              {/* Bill Upload */}
              <div className="space-y-2">
                <Label>Upload Bill (PDF/Image)</Label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${billFile ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                      {billFile ? (
                        <div className="flex items-center justify-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-primary" />
                          <span className="font-medium truncate">{billFile.name}</span>
                          <span className="text-muted-foreground">({(billFile.size / 1024).toFixed(0)} KB)</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <Upload className="w-6 h-6 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Click to upload bill</span>
                          <span className="text-xs text-muted-foreground">PDF, PNG, JPG (max 10MB)</span>
                        </div>
                      )}
                    </div>
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileChange} className="hidden" data-testid="expense-bill-upload" />
                  </label>
                  {billFile && (
                    <Button type="button" size="icon" variant="ghost" onClick={() => setBillFile(null)} className="shrink-0"><X className="w-4 h-4" /></Button>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting || uploading} data-testid="submit-expense">
                  {(submitting || uploading) ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{uploading ? 'Uploading...' : 'Submitting...'}</> : 'Submit'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Pending</p><p className="text-2xl font-bold">INR {totalPending.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Approved</p><p className="text-2xl font-bold text-[hsl(var(--success))]">INR {totalApproved.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total Claims</p><p className="text-2xl font-bold">{expenses.length}</p></CardContent></Card>
      </div>

      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter by status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader><CardTitle>Expense Claims</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {isManager && <th>Employee</th>}
                  <th>Category</th>
                  <th>Description</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                  <th>Bill</th>
                  <th>Status</th>
                  {isManager && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td>{new Date(expense.date).toLocaleDateString()}</td>
                    {isManager && <td className="font-medium text-sm">{expense.employee_name || '—'}</td>}
                    <td>{getCategoryBadge(expense.category)}</td>
                    <td className="max-w-xs truncate">{expense.description}</td>
                    <td>{expense.vendor || '-'}</td>
                    <td className="font-mono">INR {expense.amount.toLocaleString()}</td>
                    <td>
                      {expense.receipt_url ? (
                        <Badge variant="outline" className="gap-1 cursor-pointer text-primary" onClick={() => window.open(`${API_URL}/api/files/${expense.receipt_url}`, '_blank')}>
                          <FileText className="w-3 h-3" /> View
                        </Badge>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td>{getStatusBadge(expense.status)}</td>
                    {isManager && expense.status === 'pending' && (
                      <td>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleApproval(expense.id, 'approved')}><Check className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" className="gap-1 text-destructive" onClick={() => handleApproval(expense.id, 'rejected')}><X className="w-3 h-3" /></Button>
                        </div>
                      </td>
                    )}
                    {isManager && expense.status !== 'pending' && <td>-</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {expenses.length === 0 && <p className="text-center text-muted-foreground py-8">No expenses found</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpensesPage;
